import { useState, useEffect, useCallback, memo, useRef } from 'react'
import { useSettings } from '../context'
import Sidebar from './Sidebar'
import Toolbar from './Toolbar'
import LogViewer from './LogViewer'
import StatusBar from './StatusBar'
import SettingsModal from './SettingsModal'
import ToastContainer from './ToastContainer'

export default memo(function App() {
  const { settings } = useSettings()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarVisible, setSidebarVisible] = useState(true) // Desktop sidebar visibility
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [levelFilters, setLevelFilters] = useState(new Set(['ERROR', 'WARN', 'INFO', 'DEBUG', 'OTHER']))
  const [autoScroll, setAutoScroll] = useState(true)
  const sidebarSearchRef = useRef(null)

  // Apply theme
  useEffect(() => {
    document.body.className = `theme-${settings.theme}`
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) {
      const colors = {
        terminal: '#0c0c0c',
        dracula: '#21222c',
        monokai: '#1e1f1c',
        solarized: '#002b36',
        amoled: '#000000'
      }
      meta.content = colors[settings.theme] || colors.terminal
    }
  }, [settings.theme])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase()

      // Ctrl/Cmd + F - Focus log search
      if ((e.ctrlKey || e.metaKey) && key === 'f') {
        e.preventDefault()
        const desktopInput = document.getElementById('desktop-log-search')
        const mobileInput = document.getElementById('mobile-log-search')
        const targetInput = (desktopInput?.offsetParent !== null) ? desktopInput : mobileInput
        if (targetInput) {
          targetInput.focus()
          targetInput.select()
          targetInput.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }

      // Ctrl/Cmd + K - Focus session search & open sidebar
      if ((e.ctrlKey || e.metaKey) && key === 'k') {
        e.preventDefault()
        setSidebarOpen(true)
        setSidebarVisible(true)
        setTimeout(() => {
          sidebarSearchRef.current?.focus()
          sidebarSearchRef.current?.select()
        }, 100)
      }

      // Ctrl/Cmd + , - Open settings
      if ((e.ctrlKey || e.metaKey) && key === ',') {
        e.preventDefault()
        setSettingsOpen(true)
      }

      // Ctrl/Cmd + B - Toggle sidebar (desktop)
      if ((e.ctrlKey || e.metaKey) && key === 'b') {
        e.preventDefault()
        if (window.innerWidth >= 1024) {
          setSidebarVisible(prev => !prev)
        } else {
          setSidebarOpen(prev => !prev)
        }
      }

      // Escape - Close modals/sidebar
      if (e.key === 'Escape') {
        if (settingsOpen) setSettingsOpen(false)
        else if (sidebarOpen) setSidebarOpen(false)
      }

      // Home/End - Scroll
      const logViewer = document.getElementById('log-viewer')
      if (logViewer && !e.ctrlKey && !e.metaKey) {
        if (e.key === 'Home') {
          e.preventDefault()
          logViewer.scrollTo({ top: 0, behavior: 'smooth' })
        }
        if (e.key === 'End') {
          e.preventDefault()
          logViewer.scrollTo({ top: logViewer.scrollHeight, behavior: 'smooth' })
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [settingsOpen, sidebarOpen])

  // Close mobile sidebar on resize to desktop
  useEffect(() => {
    if (!sidebarOpen) return
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [sidebarOpen])

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth >= 1024) {
      setSidebarVisible(prev => !prev)
    } else {
      setSidebarOpen(prev => !prev)
    }
  }, [])
  const closeSidebar = useCallback(() => setSidebarOpen(false), [])
  const toggleAutoScroll = useCallback(() => setAutoScroll(prev => !prev), [])
  const openSettings = useCallback(() => setSettingsOpen(true), [])
  const closeSettings = useCallback(() => setSettingsOpen(false), [])

  return (
    <div className="flex items-center justify-center h-dvh p-2 sm:p-4 lg:p-6 overflow-hidden">
      {/* Terminal Container with rounded corners */}
      <div 
        className="flex w-full h-full max-w-[1800px] overflow-hidden"
        style={{ 
          borderRadius: 'var(--radius-terminal)',
          boxShadow: 'var(--shadow-terminal)',
          border: '1px solid var(--color-border)'
        }}
      >
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen}
          isVisible={sidebarVisible}
          onClose={closeSidebar}
          searchInputRef={sidebarSearchRef}
        />

        {/* Main Content */}
        <main 
          className="flex-1 flex flex-col min-w-0 overflow-hidden"
          style={{ backgroundColor: 'var(--bg-primary)' }}
        >
          {/* Toolbar */}
          <Toolbar
            onToggleSidebar={toggleSidebar}
            sidebarOpen={sidebarOpen || sidebarVisible}
            onOpenSettings={openSettings}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            levelFilters={levelFilters}
            onLevelFilterChange={setLevelFilters}
            autoScroll={autoScroll}
            onToggleAutoScroll={toggleAutoScroll}
          />

          {/* Log Viewer */}
          <LogViewer
            searchTerm={searchTerm}
            levelFilters={levelFilters}
            autoScroll={autoScroll}
            setAutoScroll={setAutoScroll}
          />

          {/* Status Bar */}
          <StatusBar />
        </main>
      </div>

      {/* Settings Modal */}
      <SettingsModal 
        isOpen={settingsOpen}
        onClose={closeSettings}
      />

      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  )
})
